import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useCart } from '../commerce/CartContext';
import { formatCurrency, products as fallbackProducts } from '../data';
import { getCategories, getProducts } from '../services/catalogueService';

const { FiSearch, FiSliders, FiX } = FiIcons;
const PAGE_SIZE = 12;

function ShopCard({ product, index }) {
  const { addItem } = useCart();
  const requiresOption = product.variants?.length > 0;
  const unavailable = product.id ? !product.inStock : false;

  return (
    <motion.article
      className="shop-card"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.32) }}
    >
      <Link className="shop-card-image" to={`/product/${product.slug}`}>
        <img
          src={product.image}
          alt={product.imageAlt || `${product.name} saree`}
          loading="lazy"
        />
        {unavailable && <span className="stock-label">Out of stock</span>}
      </Link>
      <div className="shop-card-details">
        <div>
          {product.category?.name && <span>{product.category.name}</span>}
          <h2>{product.name}</h2>
          <p>{formatCurrency(product.price)}</p>
        </div>
        {unavailable ? (
          <button className="outline-button" disabled>Unavailable</button>
        ) : requiresOption ? (
          <Link className="outline-button" to={`/product/${product.slug}`}>
            View product
          </Link>
        ) : (
          <button className="outline-button" onClick={() => addItem(product)}>
            Add to cart
          </button>
        )}
      </div>
    </motion.article>
  );
}

function ShopFilters({ filters, categories, onChange, onClear, onClose }) {
  return (
    <aside className="shop-filters" aria-label="Catalogue filters">
      <div className="filter-heading">
        <div>
          <span className="eyebrow">Refine</span>
          <h2>Filters</h2>
        </div>
        <button className="filter-close" onClick={onClose} aria-label="Close filters">
          <SafeIcon icon={FiX} />
        </button>
      </div>

      <label className="shop-field">
        Search
        <div className="search-field">
          <SafeIcon icon={FiSearch} />
          <input
            type="search"
            value={filters.search}
            onChange={(event) => onChange('search', event.target.value)}
            placeholder="Name or SKU"
          />
        </div>
      </label>

      <label className="shop-field">
        Category
        <select
          value={filters.category}
          onChange={(event) => onChange('category', event.target.value)}
        >
          <option value="">All categories</option>
          {categories.map((category) => (
            <option value={category.slug} key={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </label>

      <label className="shop-field">
        Availability
        <select
          value={filters.availability}
          onChange={(event) => onChange('availability', event.target.value)}
        >
          <option value="">All products</option>
          <option value="in_stock">In stock</option>
          <option value="low_stock">Low stock</option>
          <option value="out_of_stock">Out of stock</option>
        </select>
      </label>

      <button className="clear-filters" type="button" onClick={onClear}>
        Clear all filters
      </button>
    </aside>
  );
}

function ShopPage() {
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    availability: '',
    sort: 'featured'
  });
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [status, setStatus] = useState('loading');
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setStatus('loading');

      getProducts({ ...filters, limit })
        .then((result) => {
          setProducts(result.products);
          setCount(result.count);
          setStatus('ready');
        })
        .catch(() => {
          const search = filters.search.toLowerCase().trim();
          const fallback = fallbackProducts.filter((product) => (
            (!filters.category || product.category?.slug === filters.category)
            && (
              !search
              || product.name.toLowerCase().includes(search)
              || product.sku.toLowerCase().includes(search)
            )
          ));
          setProducts(fallback);
          setCount(fallback.length);
          setStatus('fallback');
        });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [filters, limit]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setLimit(PAGE_SIZE);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      availability: '',
      sort: 'featured'
    });
    setLimit(PAGE_SIZE);
  };

  return (
    <main className="shop-page">
      <header className="shop-hero">
        <span className="eyebrow">Timeless Elegance</span>
        <h1>Shop The Collection</h1>
        <p>Discover ZEVI sarees curated for memorable moments.</p>
      </header>

      <div className="shop-toolbar">
        <button
          className="mobile-filter-button"
          onClick={() => setFiltersOpen(true)}
          aria-expanded={filtersOpen}
        >
          <SafeIcon icon={FiSliders} />
          Filters
        </button>
        <p>{status === 'loading' ? 'Curating…' : `${count} pieces`}</p>
        <label>
          <span>Sort by</span>
          <select
            value={filters.sort}
            onChange={(event) => updateFilter('sort', event.target.value)}
          >
            <option value="featured">Featured</option>
            <option value="newest">Newest</option>
            <option value="price_low">Price: low to high</option>
            <option value="price_high">Price: high to low</option>
          </select>
        </label>
      </div>

      <div className="shop-layout">
        <div className={`filter-panel ${filtersOpen ? 'open' : ''}`}>
          <button
            className="filter-backdrop"
            onClick={() => setFiltersOpen(false)}
            aria-label="Close filters"
          />
          <ShopFilters
            filters={filters}
            categories={categories}
            onChange={updateFilter}
            onClear={clearFilters}
            onClose={() => setFiltersOpen(false)}
          />
        </div>

        <section className="shop-results" aria-live="polite">
          {status === 'loading' ? (
            <div className="catalogue-loading" role="status">
              <span />
              <p>Curating the collection…</p>
            </div>
          ) : products.length === 0 ? (
            <div className="catalogue-empty">
              <h2>No pieces found</h2>
              <p>Try changing or clearing your selected filters.</p>
              <button className="gold-button" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          ) : (
            <>
              {status === 'fallback' && (
                <p className="shop-notice">
                  Live availability is temporarily unavailable. Stock will be
                  verified before checkout.
                </p>
              )}
              <div className="shop-grid">
                {products.map((product, index) => (
                  <ShopCard
                    key={product.id || product.slug}
                    product={product}
                    index={index}
                  />
                ))}
              </div>
              {products.length < count && (
                <button
                  className="load-more"
                  onClick={() => setLimit((current) => current + PAGE_SIZE)}
                >
                  Load more
                </button>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}

export default ShopPage;